import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRight, MapPin, Calculator, Send, MessageSquare, 
  FileCheck, Star, Zap, Award, Heart, Shield, TrendingUp,
  Check, Clock, Users, Headphones, Package, Crown, Sparkles
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FreelancerApplicationModal from '../components/FreelancerApplicationModal';

const Services = () => {
  const navigate = useNavigate();
  const [showApplicationModal, setShowApplicationModal] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');

  // Process steps - RevoCut als Vermittler - ALLE in einheitlichem RevoCut Blau
  const processSteps = [
    {
      step: '01',
      icon: MapPin,
      title: 'Freelancer entdecken',
      description: 'Durchsuche unsere Plattform und finde Video-Editoren, Motion Designer und Post-Production Spezialisten. Wir zeigen dir die besten Talente.',
      features: ['Kuratierte Freelancer', 'Geprüfte Profile', 'Transparente Bewertungen'],
      color: '#5C63FF'
    },
    {
      step: '02',
      icon: Calculator,
      title: 'Preis kalkulieren',
      description: 'Nutze unseren transparenten Preisrechner, um die Kosten für dein Projekt zu berechnen. Faire Preise ohne versteckte Gebühren.',
      features: ['Transparente Preise', 'Video-Länge & Format', 'Sofort-Kalkulation'],
      color: '#5C63FF'
    },
    {
      step: '03',
      icon: Send,
      title: 'Anfrage an RevoCut',
      description: 'Sende deine Projektanfrage an uns. Wir kümmern uns um alles Weitere und finden den perfekten Freelancer für dein Projekt.',
      features: ['Projektbeschreibung', 'Wunschtermin', 'Budget angeben'],
      color: '#5C63FF'
    },
    {
      step: '04',
      icon: Headphones,
      title: 'Wir koordinieren',
      description: 'RevoCut übernimmt die komplette Koordination. Wir briefen den Freelancer, klären alle Details und halten dich auf dem Laufenden.',
      features: ['Persönlicher Ansprechpartner', 'Qualitätskontrolle', 'Regelmäßige Updates'],
      color: '#5C63FF'
    },
    {
      step: '05',
      icon: Package,
      title: 'Projekt erhalten',
      description: 'Du erhältst dein fertiges Projekt direkt von RevoCut. Wir prüfen die Qualität und stellen sicher, dass alles passt.',
      features: ['Qualitätsgeprüft', 'Revisionen inklusive', 'Finale Dateien'],
      color: '#5C63FF'
    }
  ];

  // 10 Level System - based on total revenue (Gesamtumsatz)
  const sellerLevels = [
    { level: 1, revenue: 'Start', color: '#16A34A' },
    { level: 2, revenue: '500€+', color: '#14B8A6' },
    { level: 3, revenue: '5.000€+', color: '#14B8A6' },
    { level: 4, revenue: '10.000€+', color: '#2563EB' },
    { level: 5, revenue: '15.000€+', color: '#2563EB' },
    { level: 6, revenue: '25.000€+', color: '#F97316' },
    { level: 7, revenue: '35.000€+', color: '#F97316' },
    { level: 8, revenue: '50.000€+', color: '#DC2626' },
    { level: 9, revenue: '75.000€+', color: '#DC2626' },
    { level: 10, revenue: '100.000€+', color: '#EC4899' },
  ];

  // Premium Packages
  const premiumPackages = [
    {
      name: 'Basic',
      monthlyPrice: '9,99€',
      yearlyPrice: '7,99€',
      yearlySaving: '24€',
      color: '#6366F1',
      features: [
        'Profil-Badge',
        'Basis-Analytics',
        'Priorisierter Support',
      ],
      highlight: false
    },
    {
      name: 'Pro',
      monthlyPrice: '24,99€',
      yearlyPrice: '19,99€',
      yearlySaving: '60€',
      color: '#8B5CF6',
      features: [
        'Alles aus Basic',
        'Erweiterte Analytics',
        'Höhere Sichtbarkeit',
        'Featured-Platzierung',
      ],
      highlight: true
    },
    {
      name: 'Elite',
      monthlyPrice: '49,99€',
      yearlyPrice: '39,99€',
      yearlySaving: '120€',
      color: '#A855F7',
      features: [
        'Alles aus Pro',
        'Top-Platzierung',
        'Exklusive Aufträge',
        'Persönlicher Manager',
        'Premium-Badge',
      ],
      highlight: false
    }
  ];

  return (
    <div className="min-h-screen text-white" style={{ backgroundColor: '#0a0a0a' }}>
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-6 md:px-12 lg:px-20 xl:px-32 2xl:px-44">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            So funktioniert{' '}
            <span style={{ color: '#5C63FF' }}>RevoCut</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
            Wir vermitteln dein Projekt an die besten Freelancer – du hast einen Ansprechpartner, wir kümmern uns um den Rest.
          </p>
        </div>
      </section>

      {/* Value Proposition */}
      <section className="px-6 md:px-12 lg:px-20 xl:px-32 2xl:px-44 pb-12">
        <div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 text-center">
              <Headphones className="w-10 h-10 mx-auto mb-4" style={{ color: '#5C63FF' }} />
              <h3 className="font-bold mb-2">Ein Ansprechpartner</h3>
              <p className="text-sm text-gray-400">Du kommunizierst nur mit uns – wir regeln alles mit dem Freelancer.</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 text-center">
              <Shield className="w-10 h-10 mx-auto mb-4" style={{ color: '#5C63FF' }} />
              <h3 className="font-bold mb-2">Qualitätsgarantie</h3>
              <p className="text-sm text-gray-400">Wir prüfen jedes Projekt bevor du es erhältst.</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10 text-center">
              <TrendingUp className="w-10 h-10 mx-auto mb-4" style={{ color: '#5C63FF' }} />
              <h3 className="font-bold mb-2">Geprüfte Freelancer</h3>
              <p className="text-sm text-gray-400">Nur die besten Talente schaffen es auf unsere Plattform.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="px-6 md:px-12 lg:px-20 xl:px-32 2xl:px-44 py-16">
        <div>
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Der <span style={{ color: '#5C63FF' }}>Ablauf</span>
          </h2>

          {/* Desktop Timeline */}
          <div className="hidden md:block relative">
            {/* Vertical Line - einheitlich RevoCut Blau */}
            <div 
              className="absolute left-[39px] top-0 bottom-0 w-[2px]"
              style={{ backgroundColor: '#5C63FF' }}
            />
            
            <div className="space-y-12">
              {processSteps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <div key={index} className="relative flex gap-8 items-start">
                    {/* Step Number Circle */}
                    <div 
                      className="w-20 h-20 rounded-full flex items-center justify-center flex-shrink-0 z-10 border-4"
                      style={{ 
                        backgroundColor: '#0a0a0a',
                        borderColor: step.color,
                        boxShadow: `0 0 20px ${step.color}30`
                      }}
                    >
                      <span 
                        className="text-2xl font-bold"
                        style={{ fontFamily: 'Montserrat, sans-serif', color: step.color }}
                      >
                        {step.step}
                      </span>
                    </div>
                    
                    {/* Content Card */}
                    <div 
                      className="flex-1 p-6 rounded-2xl border border-white/10 hover:border-white/20 transition-all"
                      style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
                    >
                      <div className="flex items-start gap-4 mb-4">
                        <div 
                          className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                          style={{ backgroundColor: `${step.color}20` }}
                        >
                          <Icon className="w-6 h-6" style={{ color: step.color }} />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                            {step.title}
                          </h3>
                          <p className="text-gray-400 text-sm leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-4 pl-16">
                        {step.features.map((feature, i) => (
                          <span 
                            key={i}
                            className="px-3 py-1 rounded-full text-xs"
                            style={{ backgroundColor: `${step.color}15`, color: step.color }}
                          >
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Mobile Timeline */}
          <div className="md:hidden space-y-6">
            {processSteps.map((step, index) => {
              const Icon = step.icon;
              return (
                <div 
                  key={index}
                  className="p-6 rounded-2xl border border-white/10"
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.02)' }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div 
                      className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: `${step.color}20`, border: `2px solid ${step.color}` }}
                    >
                      <span className="text-lg font-bold" style={{ color: step.color }}>{step.step}</span>
                    </div>
                    <h3 className="text-lg font-bold" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-gray-400 text-sm mb-4">{step.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {step.features.map((feature, i) => (
                      <span 
                        key={i}
                        className="px-3 py-1 rounded-full text-xs"
                        style={{ backgroundColor: `${step.color}15`, color: step.color }}
                      >
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="text-center mt-12">
            <button
              data-testid="process-start-project-btn"
              onClick={() => {
                navigate('/', { state: { scrollTo: 'map' } });
              }}
              className="px-8 py-4 rounded-full text-base font-semibold transition-all hover:scale-105 inline-flex items-center justify-center gap-2"
              style={{ backgroundColor: '#5C63FF' }}
            >
              Projekt starten
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Platform Features - For Freelancers */}
      <section className="px-6 md:px-12 lg:px-20 xl:px-32 2xl:px-44 py-16 border-t border-white/5">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
            Für <span style={{ color: '#5C63FF' }}>Freelancer</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto">
            Du bist Video-Editor oder Motion Designer? So funktioniert RevoCut für Freelancer.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Rating System */}
          <div className="p-8 rounded-2xl border border-white/10 bg-white/[0.02]">
            <div className="flex items-start gap-4 mb-6">
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'rgba(250, 204, 21, 0.15)' }}
              >
                <Star className="w-7 h-7 text-yellow-400" />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Bewertungssystem
                </h3>
                <p className="text-gray-400 text-sm">
                  Nach jedem Projekt bewertet RevoCut deine Arbeit. Gute Bewertungen = mehr Aufträge.
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm">
                <div className="flex">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <span className="text-gray-300">1-5 Sterne Bewertung</span>
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <Check className="w-4 h-4 text-yellow-400" />
                Qualitätsbewertung durch RevoCut
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <Check className="w-4 h-4 text-yellow-400" />
                Durchschnittsbewertung auf deinem Profil
              </div>
            </div>
          </div>

          {/* Aufträge erhalten */}
          <div className="p-8 rounded-2xl border border-white/10 bg-white/[0.02]">
            <div className="flex items-start gap-4 mb-6">
              <div 
                className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: 'rgba(92, 99, 255, 0.2)' }}
              >
                <Package className="w-7 h-7" style={{ color: '#5C63FF' }} />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                  Aufträge erhalten
                </h3>
                <p className="text-gray-400 text-sm">
                  RevoCut vermittelt dir passende Projekte basierend auf deinen Skills.
                </p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3 text-sm text-gray-300">
                <Check className="w-4 h-4" style={{ color: '#5C63FF' }} />
                Wir finden die passenden Projekte für dich
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <Check className="w-4 h-4" style={{ color: '#5C63FF' }} />
                Klare Briefings von RevoCut
              </div>
              <div className="flex items-center gap-3 text-sm text-gray-400">
                <Check className="w-4 h-4" style={{ color: '#5C63FF' }} />
                Sichere Bezahlung nach Projektabschluss
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 10 Level System */}
      <section className="px-6 md:px-12 lg:px-20 xl:px-32 2xl:px-44 py-16">
        <div>
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              Das <span style={{ color: '#10B981' }}>10-Level</span> System
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Je mehr Umsatz du generierst, desto höher steigst du im Level. Höhere Level bedeuten mehr Sichtbarkeit und bessere Konditionen.
            </p>
          </div>

          {/* Level Grid */}
          <div className="grid grid-cols-5 md:grid-cols-10 gap-2 md:gap-3 mb-8">
            {sellerLevels.map((lvl) => (
              <div
                key={lvl.level}
                className="p-3 md:p-4 rounded-xl text-center transition-all hover:scale-105"
                style={{ 
                  backgroundColor: `${lvl.color}15`,
                  border: `1px solid ${lvl.color}40`
                }}
              >
                <div 
                  className="text-lg md:text-2xl font-bold mb-1"
                  style={{ color: lvl.color, fontFamily: 'Montserrat, sans-serif' }}
                >
                  {lvl.level}
                </div>
                <div className="text-[10px] md:text-xs text-gray-400">{lvl.revenue}</div>
              </div>
            ))}
          </div>

          {/* Level Benefits */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <TrendingUp className="w-8 h-8 mb-4" style={{ color: '#5C63FF' }} />
              <h3 className="font-bold mb-2">Mehr Sichtbarkeit</h3>
              <p className="text-sm text-gray-400">Höhere Level werden in der Suche bevorzugt angezeigt.</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <Award className="w-8 h-8 mb-4" style={{ color: '#5C63FF' }} />
              <h3 className="font-bold mb-2">Level-Badges</h3>
              <p className="text-sm text-gray-400">Zeige Kunden deine Erfahrung mit sichtbaren Badges.</p>
            </div>
            <div className="p-6 rounded-2xl bg-white/[0.02] border border-white/10">
              <Users className="w-8 h-8 mb-4" style={{ color: '#5C63FF' }} />
              <h3 className="font-bold mb-2">Bevorzugte Vermittlung</h3>
              <p className="text-sm text-gray-400">Ab Level 5 wirst du bevorzugt für neue Projekte vorgeschlagen und erhältst Zugang zu exklusiven Aufträgen.</p>
            </div>
          </div>

          {/* CTA Button */}
          <div className="text-center">
            <button
              data-testid="level-apply-freelancer-btn"
              onClick={() => setShowApplicationModal(true)}
              className="px-8 py-4 rounded-full text-base font-semibold transition-all hover:scale-105 inline-flex items-center justify-center gap-2"
              style={{ backgroundColor: '#5C63FF' }}
            >
              Als Freelancer bewerben
              <ArrowRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* Premium Membership */}
      <section className="px-6 md:px-12 lg:px-20 xl:px-32 2xl:px-44 py-16">
        <div>
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Montserrat, sans-serif' }}>
              <span style={{ color: '#5C63FF' }}>Premium</span> Membership
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto mb-8">
              Erweitere deine Möglichkeiten mit unseren Premium-Paketen. Mehr Sichtbarkeit, bessere Analytics und exklusive Features.
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/[0.05] border border-white/10" data-testid="billing-toggle">
              <button
                data-testid="billing-monthly"
                onClick={() => setBillingPeriod('monthly')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all ${
                  billingPeriod === 'monthly'
                    ? 'bg-white/15 text-white'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                Monatlich
              </button>
              <button
                data-testid="billing-yearly"
                onClick={() => setBillingPeriod('yearly')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                  billingPeriod === 'yearly'
                    ? 'bg-white/15 text-white'
                    : 'text-gray-400 hover:text-gray-300'
                }`}
              >
                Jährlich
                <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/20 text-green-400 font-bold">-20%</span>
              </button>
            </div>
          </div>

          {/* Premium Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {premiumPackages.map((pkg, index) => (
              <div
                key={index}
                className={`p-6 rounded-2xl border transition-all ${
                  pkg.highlight 
                    ? 'border-purple-500/50 bg-gradient-to-b from-purple-500/10 to-transparent scale-105' 
                    : 'border-white/10 bg-white/[0.02]'
                }`}
              >
                {pkg.highlight && (
                  <div className="text-center mb-4">
                    <span className="px-3 py-1 text-xs font-bold rounded-full bg-purple-500/30 text-purple-300">
                      BELIEBT
                    </span>
                  </div>
                )}
                <div className="text-center mb-6">
                  <Crown className="w-10 h-10 mx-auto mb-3" style={{ color: pkg.color }} />
                  <h3 className="text-xl font-bold mb-2" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                    {pkg.name}
                  </h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-3xl font-bold" style={{ color: pkg.color }}>
                      {billingPeriod === 'monthly' ? pkg.monthlyPrice : pkg.yearlyPrice}
                    </span>
                    <span className="text-gray-500 text-sm">/Monat</span>
                  </div>
                  {billingPeriod === 'yearly' && (
                    <p className="text-xs text-green-400 mt-2">Spare {pkg.yearlySaving} pro Jahr</p>
                  )}
                </div>
                <ul className="space-y-3 mb-6">
                  {pkg.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-gray-300">
                      <Check className="w-4 h-4 text-green-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => navigate('/login?type=freelancer')}
                  className={`w-full py-3 rounded-xl font-medium transition-all ${
                    pkg.highlight
                      ? 'bg-purple-500 hover:bg-purple-400 text-white'
                      : 'bg-white/10 hover:bg-white/20 text-white'
                  }`}
                >
                  Jetzt starten
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Freelancer Application Modal */}
      <FreelancerApplicationModal 
        isOpen={showApplicationModal} 
        onClose={() => setShowApplicationModal(false)} 
      />

      <Footer />
    </div>
  );
};

export default Services;
